import { memo, useMemo } from 'react';
import { format } from 'date-fns';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TableContainer from '@mui/material/TableContainer';
import { Typography } from '@components/Typography';
import { Table } from '@components/Table';
import { useTranslation, useLocations, useTableSort } from '@hooks';
import { analyticsService } from '@services';
import { formatNumber, formatOptionalNumber, formatOptionalPercent } from '@helpers/formatNumbers';
import ExportCSV from '@features/Analytics/ExportCSV';

const SORT_COLUMNS = {
    location: 'internalName',
    totalReviews: 'totalReviews',
    totalRepliesPercentage: 'totalRepliesPercentage',
    averageRating: 'averageRating',
    reviewIndex: 'reviewIndex',
};

const DEFAULT_SORT = { column: 'location', dir: 'asc' };

const Container = styled(Paper)({
    padding: '24px',
    width: '100%',
    borderRadius: 8,
    marginTop: '24px',
});

const TableCellText = styled(Typography)({
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    alignItems: 'center',
});

const Text = styled('span', { shouldForwardProp: prop => prop !== 'withIcon' })(({ withIcon }) => ({
    marginRight: '28px',
    ...(withIcon && {
        marginRight: '8px',
    }),
}));

const getTableHeader = t => {
    return [
        {
            key: 0,
            sortKey: 'location',
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('common.location')}
                </Typography>
            ),
        },
        {
            key: 1,
            sortKey: 'totalReviews',
            align: 'right',
            component: (
                <Typography variant="subtitle2" align="right">
                    {t('AnalyticsReviews.totalReviews')}
                </Typography>
            ),
        },
        {
            key: 2,
            sortKey: 'totalRepliesPercentage',
            align: 'right',
            component: (
                <Typography variant="subtitle2" align="right">
                    {t('AnalyticsReviews.totalRepliesPercentage')}
                </Typography>
            ),
        },
        {
            key: 3,
            sortKey: 'averageRating',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsReviews.averageRating')}
                </Typography>
            ),
        },
        {
            key: 4,
            sortKey: 'reviewIndex',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsReviews.reviewIndex')}
                </Typography>
            ),
        },
    ];
};

const getTableArrow = field => {
    if (field > 0) {
        return <ArrowUpwardIcon sx={{ color: 'var(--success-main)', fontSize: '20px' }} />;
    } else if (field < 0) {
        return <ArrowDownwardIcon sx={{ color: 'var(--error-main)', fontSize: '20px' }} />;
    }
    return null;
};

const getTableBody = data => {
    return data.map(
        ({
            internalName,
            totalReviews,
            totalReviewsChange,
            totalRepliesPercentage,
            totalRepliesChange,
            averageRating,
            averageRatingChange,
            reviewIndex,
            reviewIndexChange,
        }) => [
            {
                component: (
                    <Typography variant="subtitle2" align="left">
                        {internalName}
                    </Typography>
                ),
            },
            {
                component: (
                    <TableCellText variant="body2" align="right">
                        <Text withIcon={totalReviewsChange !== 0}>{formatNumber(totalReviews)}</Text>
                        {getTableArrow(totalReviewsChange)}
                    </TableCellText>
                ),
            },
            {
                component: (
                    <TableCellText variant="body2" align="right">
                        <Text withIcon={totalRepliesChange !== 0}>{formatOptionalPercent(totalRepliesPercentage)}</Text>
                        {getTableArrow(totalRepliesChange)}
                    </TableCellText>
                ),
            },
            {
                component: (
                    <TableCellText variant="body2" align="right">
                        <Text withIcon={averageRatingChange !== 0}>{formatOptionalNumber(averageRating)}</Text>
                        {getTableArrow(averageRatingChange)}
                    </TableCellText>
                ),
            },
            {
                component: (
                    <TableCellText variant="body2" align="right">
                        <Text withIcon={reviewIndexChange !== 0}>{formatOptionalNumber(reviewIndex)}</Text>
                        {getTableArrow(reviewIndexChange)}
                    </TableCellText>
                ),
            },
        ],
    );
};

const ReviewsTable = memo(({ isLoading, data, filter }) => {
    const { locationsMap } = useLocations();
    const { t } = useTranslation();

    const dataWithLocationNames = useMemo(
        () =>
            data.map(item => ({
                ...item,
                internalName: locationsMap[item.locationId]?.internalName || '',
            })),
        [data, locationsMap],
    );

    const { sort, onSortChange, sortedData } = useTableSort(dataWithLocationNames, {
        columns: SORT_COLUMNS,
        defaultSort: DEFAULT_SORT,
    });

    const onExport = exportFormat =>
        analyticsService.exportAnalyticsCSV(
            {
                ...filter,
                profileTypes: filter.integrations,
                locations: filter.locations.map(item => item.id),
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            'reviews',
            null,
            exportFormat,
        );

    return (
        <Container elevation={1}>
            <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}
            >
                <Typography variant="title1" sx={{ alignSelf: 'center' }}>
                    {t(`Header.analyticsReviews`)}
                </Typography>
                <ExportCSV type="reviews" apiCall={onExport} />
            </div>
            <TableContainer>
                <Table
                    data={sortedData}
                    columnNames={getTableHeader(t)}
                    rows={getTableBody(sortedData)}
                    isDataLoading={isLoading}
                    sort={sort}
                    onSortChange={onSortChange}
                />
            </TableContainer>
        </Container>
    );
});

ReviewsTable.displayName = 'ReviewsTable';

export default ReviewsTable;
